import React, { useEffect, useRef, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Upload, X, FileText, Image as ImageIcon, Loader2, Eye } from "lucide-react";
import { toast } from "sonner";

/**
 * Upload/preview cell for a file. `value` is a file_id or null.
 *
 * Loading strategy (lazy):
 *   - On mount, fetches only /files/{id}/meta (a few hundred bytes).
 *   - The blob is fetched ONLY when the user clicks "Preview".
 */
export default function FileCell({ value, onChange, testId, accept = "image/*,.pdf" }) {
  const inputRef = useRef(null);
  const cancelRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [meta, setMeta] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);

  // Fetch only metadata on mount / when value changes
  useEffect(() => {
    let cancelled = false;
    async function loadMeta() {
      setMeta(null);
      if (!value) return;
      try {
        const { data } = await api.get(`/files/${value}/meta`);
        if (!cancelled) setMeta(data);
      } catch {
        if (!cancelled) setMeta({ error: true });
      }
    }
    loadMeta();
    return () => { cancelled = true; };
  }, [value]);

  // Clean up blob URL when value/preview changes; also cancel any in-flight preview fetch on unmount
  useEffect(() => {
    return () => {
      if (cancelRef.current) cancelRef.current();
      if (previewUrl) URL.revokeObjectURL(previewUrl);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [previewUrl]);

  const openPreview = async () => {
    if (previewUrl) {
      window.open(previewUrl, "_blank", "noopener");
      return;
    }
    if (!value) return;
    setPreviewLoading(true);
    let cancelled = false;
    // If the row unmounts mid-fetch, this flag prevents the state update
    // and lets us revoke the newly-created blob URL.
    const guard = { get cancelled() { return cancelled; } };
    cancelRef.current = () => { cancelled = true; };
    try {
      const { data } = await api.get(`/files/${value}`, { responseType: "blob" });
      if (guard.cancelled) {
        // Component unmounted — nothing to do, blob was never assigned
        return;
      }
      const url = URL.createObjectURL(data);
      setPreviewUrl(url);
      window.open(url, "_blank", "noopener");
    } catch (e) {
      if (!guard.cancelled) {
        toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to load file");
      }
    } finally {
      if (!guard.cancelled) setPreviewLoading(false);
      cancelRef.current = null;
    }
  };

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const form = new FormData();
      form.append("file", file);
      const { data } = await api.post("/files/upload", form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      // reset local preview state before switching value
      if (previewUrl) {
        URL.revokeObjectURL(previewUrl);
        setPreviewUrl(null);
      }
      onChange(data.id);
      toast.success("Uploaded");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Upload failed");
    } finally {
      setUploading(false);
      if (inputRef.current) inputRef.current.value = "";
    }
  };

  const clear = () => {
    if (previewUrl) URL.revokeObjectURL(previewUrl);
    setPreviewUrl(null);
    onChange(null);
  };

  const isImage = meta?.content_type?.startsWith("image/");
  const IconEl = isImage ? ImageIcon : FileText;

  return (
    <div className="flex items-center gap-1.5" data-testid={testId}>
      <input
        ref={inputRef}
        type="file"
        className="hidden"
        accept={accept}
        onChange={onFile}
        data-testid={testId ? `${testId}-input` : undefined}
      />

      {value && meta && !meta.error && (
        <button
          type="button"
          onClick={openPreview}
          disabled={previewLoading}
          className="inline-flex items-center gap-1.5 h-9 px-2.5 rounded border border-border/70 bg-background text-xs text-espresso hover:bg-secondary/60 max-w-[160px]"
          title={meta.original_filename || "Open"}
          data-testid={testId ? `${testId}-preview` : undefined}
        >
          {previewLoading ? <Loader2 className="h-3.5 w-3.5 animate-spin shrink-0" /> : <Eye className="h-3.5 w-3.5 shrink-0" />}
          <IconEl className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
          <span className="truncate">{meta.original_filename || (isImage ? "image" : "file")}</span>
        </button>
      )}

      <button
        type="button"
        onClick={() => inputRef.current?.click()}
        className="inline-flex items-center gap-1 h-9 px-2.5 rounded border border-border/70 bg-background text-xs text-espresso hover:bg-secondary/60 disabled:opacity-60"
        disabled={uploading}
        data-testid={testId ? `${testId}-upload` : undefined}
      >
        {uploading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Upload className="h-3.5 w-3.5" />}
        <span className="hidden xl:inline">{value ? "Replace" : "Upload"}</span>
      </button>

      {value && (
        <button
          type="button"
          onClick={clear}
          className="inline-flex items-center h-9 w-9 justify-center rounded border border-border/70 bg-background text-muted-foreground hover:text-destructive"
          title="Remove file"
          data-testid={testId ? `${testId}-remove` : undefined}
        >
          <X className="h-3.5 w-3.5" />
        </button>
      )}
    </div>
  );
}
